import { ApolloClient, NormalizedCacheObject } from '@apollo/client'
import { action, computed, makeObservable, observable } from 'mobx'

import {
  DeliveryFilterType,
  IDeliveryConfigurations,
  ILogisticsConfigurations,
  IOrderedSitemap,
  IPreciseLocation,
  IProjectAddress,
  IProjectColoringOptions,
  IProjectHistory,
  IProjectMaterialOptions,
  IProjectStatusUpdateOptions,
  IProjectTypeOptions,
  IRemoteTileset,
  ISiteLocation,
  ITileset,
  ITilesetsConfiguration,
  IUploadLog,
  IWorkflowConfigurations,
  IZoneMapThresholds,
  RecentlyUpdatedMode,
  ThresholdUnit,
} from '~/client/graph'
import FieldIds from '~/client/src/shared/enums/DeliveryFieldIds'
import FlaggedFeatures from '~/client/src/shared/enums/FlaggedFeatures'
import IDateTimeFilter from '~/client/src/shared/interfaces/IDateTimeFilter'
import AssociationItem from '~/client/src/shared/models/AssociationItem/AssociationItem'
import Building from '~/client/src/shared/models/LocationObjects/Building'
import Gate from '~/client/src/shared/models/LocationObjects/Gate'
import LogisticsObject from '~/client/src/shared/models/LocationObjects/LogisticsObject'
import OffloadingEquipment from '~/client/src/shared/models/LocationObjects/OffloadingEquipment'
import OtherLocation from '~/client/src/shared/models/LocationObjects/OtherLocation'
import Route from '~/client/src/shared/models/LocationObjects/Route'
import OperationRule from '~/client/src/shared/models/OperationRule'
import Project from '~/client/src/shared/models/Project'
import Sitemap from '~/client/src/shared/models/Sitemap'
import User from '~/client/src/shared/models/User'
import type { IWorkflowDto } from '~/client/src/shared/models/Workflow'
import CommonDeliveryAttributeDto from '~/client/src/shared/types/CommonDeliveryAttributeDto'
import CustomDeliveryListFilter from '~/client/src/shared/types/CustomDeliveryListFilter'
import MobXMap from '~/client/src/shared/types/MobXMap'
import SimpleCalendarDto from '~/client/src/shared/types/SimpleCalendarDto'

import Localization from '../../shared/localization/LocalizationManager'
import {
  DeliveryPageMode,
  DeliveryPageViewMode,
} from '../components/Deliveries/DeliveriesView.store'
import { IMapConfiguration } from '../components/SitemapsGallery/MapViewsGallery'
import DeliveryGroupingOption from '../enums/DeliveryGroupingOption'
import FlaggedFormCategories from '../enums/FlaggedFormCategories'
import { IntegrationAppName } from '../enums/IntegrationAppName'
import IUserSession from '../interfaces/IUserSession'
import Assignment from '../models/Assignment/Assignment'
import Basemap from '../models/Basemap'
import Closure from '../models/Closure'
import Delivery from '../models/Delivery'
import GlobeView from '../models/GlobeView'
import IIntegrationConfig from '../models/IIntegrationConfig'
import Area from '../models/LocationObjects/Area'
import InteriorDoor from '../models/LocationObjects/InteriorDoor'
import InteriorPath from '../models/LocationObjects/InteriorPath'
import Level from '../models/LocationObjects/Level'
import LocationIntegration from '../models/LocationObjects/LocationIntegration'
import ProjectLocation from '../models/LocationObjects/ProjectLocation'
import Staging from '../models/LocationObjects/Staging'
import VerticalObject from '../models/LocationObjects/VerticalObject'
import Zone from '../models/LocationObjects/Zone'
import Markup from '../models/Markup'
import Message from '../models/Message'
import PermitInspection from '../models/PermitInspection'
import PermitInspectionChange from '../models/PermitInspectionChange'
import PermitStatusChange from '../models/PermitStatusChange'
import Photo from '../models/Photo'
import ScanHistory from '../models/ScanHistory'
import Scanner from '../models/Scanner'
import SitemapItem from '../models/SitemapItem'
import { ITag } from '../models/Tag'
import Thread from '../models/Thread'
import UserProject from '../models/UserProject'
import CustomUploadLogFilter from '../types/CustomUploadLogFilter'
import CustomWorkflowsFilter from '../types/CustomWorkflowFilter'
import { createEmptyAddress } from '../utils/Address'
import { convertToBytes } from '../utils/converters'
import DEFAULT_DELIVERY_CARD_CONFIG from '../utils/defaultDeliveryCardConfig'
import PRE_POPULATED_COLORS from '../utils/deliveryPrepopulatedColors'
import isTestEnvironment from '../utils/environment'
import isIntegrationConfigValid from '../utils/isIntegrationConfigValid'
import { IWorkflowCarryPlan } from '../utils/workflowTypeSwitchCarry'
import UIFilterInfo from './substates/UIFilterInfo'

interface IDeliverySettings {
  deliveryFieldsConfigId: string
  mandatoryFields: { [fieldName: string]: boolean }
  defaultMandatoryFields: { [fieldName: string]: boolean }
  configurations: IDeliveryConfigurations
  compactView: CompactDeliveryView
  customFieldNames: { [fieldName: string]: string }
  hiddenFields: { [fieldName: string]: boolean }
  defaultHiddenFields: { [fieldName: string]: boolean }
  selectedSitemapId?: string
  isDefaultsReceived: boolean
  isDataReceived: boolean
}

interface IWorkflowsSettings {
  configurations: IWorkflowConfigurations
  isDataReceived: boolean
}

interface ILogisticsSettings {
  configurations: ILogisticsConfigurations
}

const configErrorMessage = 'One of the integration configurations is incorrect'

export default class InitialState {
  @observable public graphClient: ApolloClient<NormalizedCacheObject>
  @observable public countryCode: string = ''

  @observable public activeProject = Project.none
  @observable public user: User = null
  @observable public lastTraceId: string | null = null
  /**
   * The permits the last successful SAVE_WORKFLOWS wrote, as the server returned them. Lists that
   * otherwise learn of new permits only from the site-permit subscription merge these too, so a
   * permit saved before that subscription is live still appears.
   */
  @observable.ref public lastSavedWorkflows: IWorkflowDto[] = []

  @action.bound
  public setLastTraceId(traceId: string | null) {
    this.lastTraceId = traceId
  }

  @observable public tenantUserSession: IUserSession = null

  public delivery = observable<IDeliverySettings>({
    deliveryFieldsConfigId: null,
    configurations: null,
    compactView: new CompactDeliveryView(),
    customFieldNames: {},
    mandatoryFields: {},
    defaultMandatoryFields: null,
    hiddenFields: {},
    defaultHiddenFields: null,
    isDefaultsReceived: false,
    isDataReceived: false,
  })

  public workflows = observable<IWorkflowsSettings>({
    configurations: null,
    isDataReceived: false,
  })

  public logistics = observable<ILogisticsSettings>({
    configurations: null,
  })

  public isUnauthorizedMode: boolean = false

  public loading = observable(new Map<any, boolean>()) // Loading flags for ui

  public onAuthStateChangedCallback: () => void

  private _initProjectCode: string = ''

  public get initProjectCode(): string {
    return this._initProjectCode
  }

  public set initProjectCode(code: string) {
    this._initProjectCode = code?.toUpperCase() || ''
  }

  public initPathname: string = ''
  // Full requested destination (pathname + search + hash), minus the
  // single-use auth tokens, captured once at INIT and replayed verbatim by
  // the post-login fly-over so no URL component is dropped (STX1-582).
  public initDeepLink: string = ''

  public preSelectedPermitTypeId: string = null
  public preSelectedSiteLocation: ISiteLocation = null
  // Set when a map-first drawing session hands its creation to the full form: the
  // form adopts this as its markup draft id so the shapes already drawn come with it.
  public preSelectedMarkupDraftId: string = null
  // Same hand-off, for what the user had already typed into the Composer.
  public preSelectedWorkflowCarryPlan: IWorkflowCarryPlan = null
  // Set by the form that owns a handed-off creation, so the map can move its pin.
  public applyHandedOffPreciseLocation: (
    preciseLocation: IPreciseLocation,
  ) => void = null

  @observable public didInitialize = false
  @observable public initialRoute = ''
  @observable public activeConnections = new Map()

  public deliveryRequesterCompaniesFromLastSync: string[] = []

  public deliveryFilters = observable<IFilters>({
    searchKey: '',
    groupingKey: DeliveryGroupingOption[FieldIds.DATE],
    fieldsMap: {
      [DeliveryFilterType.Status]: new UIFilterInfo(),
      [DeliveryFilterType.Company]: new UIFilterInfo(),
      [DeliveryFilterType.Project]: new UIFilterInfo(),
      [DeliveryFilterType.Gate]: new UIFilterInfo(),
      [DeliveryFilterType.Building]: new UIFilterInfo(),
      [DeliveryFilterType.Area]: new UIFilterInfo(),
      [DeliveryFilterType.Route]: new UIFilterInfo(),
      [DeliveryFilterType.Zone]: new UIFilterInfo(),
      [DeliveryFilterType.Level]: new UIFilterInfo(),
      [DeliveryFilterType.Followers]: new UIFilterInfo(),
      [DeliveryFilterType.Assigners]: new UIFilterInfo(),
      [DeliveryFilterType.Equipment]: new UIFilterInfo(),
    },
  })

  public closures = new MobXMap<Closure>()
  public deliveryReportFilters = observable<IFilters>({
    searchKey: '',
    groupingKey: DeliveryGroupingOption[FieldIds.DATE],
    fieldsMap: {
      [DeliveryFilterType.Status]: new UIFilterInfo(),
      [DeliveryFilterType.Company]: new UIFilterInfo(),
      [DeliveryFilterType.Gate]: new UIFilterInfo(),
      [DeliveryFilterType.Building]: new UIFilterInfo(),
      [DeliveryFilterType.Area]: new UIFilterInfo(),
      [DeliveryFilterType.Route]: new UIFilterInfo(),
      [DeliveryFilterType.Zone]: new UIFilterInfo(),
      [DeliveryFilterType.Level]: new UIFilterInfo(),
      [DeliveryFilterType.Followers]: new UIFilterInfo(),
      [DeliveryFilterType.Assigners]: new UIFilterInfo(),
      [DeliveryFilterType.Equipment]: new UIFilterInfo(),
    },
  })

  public scanners = observable(new Map<string, Scanner>())
  public scanHistories = observable(new Map<string, ScanHistory>())
  public permitStatusChanges = observable(new Map<string, PermitStatusChange>())
  public permitInspections = observable(new Map<string, PermitInspection>())
  public permitInspectionChanges = observable(
    new Map<string, PermitInspectionChange>(),
  )

  public basemaps = observable(new Map<string, Basemap>())
  public sitemaps = observable(new Map<string, Sitemap>())
  public tilesets = observable(new Map<string, ITileset>())
  public uploadLogs = observable(new Map<string, IUploadLog>())
  public remoteTilesets = observable(new Map<string, IRemoteTileset>())
  public globeViews = observable(new Map<string, GlobeView>())
  public sitemapItems = observable(new Map<string, SitemapItem>())
  public markups = observable(new Map<string, Markup>())
  public calendars = observable(new Map<string, SimpleCalendarDto>())

  public messages = new MobXMap<Message>()
  public photos = new MobXMap<Photo>()
  public threads = observable(new Map<string, Thread>())

  public logisticsObjects = observable(new Map<string, LogisticsObject>())
  public stagings = observable(new Map<string, Staging>())
  public interiorPaths = observable(new Map<string, InteriorPath>())
  public interiorDoors = observable(new Map<string, InteriorDoor>())
  public locationIntegrations = observable(
    new Map<string, LocationIntegration>(),
  )
  public verticalObjects = observable(new Map<string, VerticalObject>())

  public levels = observable(new Map<string, Level>())
  public areas = observable(new Map<string, Area>())
  public projectLocations = observable(new Map<string, ProjectLocation>())
  // The catch-all: places whose root kind is project-added or missing (STX1-597).
  public otherLocations = observable(new Map<string, OtherLocation>())

  public deliveries = new MobXMap<Delivery>()
  public zones = observable(new Map<string, Zone>())
  public gates = observable(new Map<string, Gate>())
  public buildings = observable(new Map<string, Building>())
  public routes = observable(new Map<string, Route>())
  public offloadingEquipments = observable(
    new Map<string, OffloadingEquipment>(),
  )
  public deliveryVehicleTypes = observable(
    new Map<string, CommonDeliveryAttributeDto>(),
  )
  public deliveryUnits = observable(
    new Map<string, CommonDeliveryAttributeDto>(),
  )
  // STX1-1057: the set-password prompt, opened by the INIT_APP_3 gate for a
  // user whose only possible credential is a password.
  @observable public isSavePasswordPromptOpen: boolean = false

  // Dismissal is deliberately held here rather than in storage: it lasts for
  // this session and the prompt returns at the next sign-in, so a user who is
  // not ready now is never permanently lost. Persisting it would also put the
  // sign-in path back on localStorage, which STX1-1060 showed can be denied.
  @observable public isSavePasswordPromptDismissed: boolean = false

  @observable public featureFlags: { [key in FlaggedFeatures]?: boolean } = {}
  @observable public tilesetsConfiguration: ITilesetsConfiguration = null
  @observable public flaggedFormCategories: {
    [key in FlaggedFormCategories]?: boolean
  } = {}
  public deliveryFollowings = observable(new Map<string, AssociationItem>())
  public sitePermitFollowings = observable(new Map<string, AssociationItem>())
  public workflowFollowings = this.sitePermitFollowings
  public deliveryAssignments = observable(new Map<string, Assignment>())
  public sitePermitAssignments = observable(new Map<string, Assignment>())
  public workflowAssignments = this.sitePermitAssignments

  public customDeliveryListFilters = observable(
    new Map<string, CustomDeliveryListFilter>(),
  )

  public customWorkflowListFilters = observable(
    new Map<string, CustomWorkflowsFilter>(),
  )

  public customUploadLogFilters = observable(
    new Map<string, CustomUploadLogFilter>(),
  )

  public operationRules = observable(new Map<string, OperationRule>())

  public projectRoles = observable(new Map<string, ITag>())
  public projectTeams = observable(new Map<string, ITag>())
  public projectTrades = observable(new Map<string, ITag>())
  public defaultProjectTeams = observable(new Map<string, ITag>())

  @observable public projectMaterialOptions: IProjectMaterialOptions = null
  @observable public projectAddress: IProjectAddress = {
    projectId: null,
  }
  @observable public projectTypeOptions: IProjectTypeOptions = {
    isDeliveriesDisabled: false,
    isTrackerDisabled: true,
    isLogisticsDisabled: false,
    isMaterialsDisabled: false,
    isMaterialResourcesDisabled: false,
    isEquipmentDisabled: false,
    isPhotosDisabled: false,
    isFormsDisabled: false,
    isAnalyticsDisabled: false,
    isStruxTagDisabled: false,
    isLocationsDisabled: false,
    projectId: null,
  }
  @observable public projectStatusUpdateOptions: IProjectStatusUpdateOptions = {
    isAutoGenerated: true,
    isManpowerDisabled: false,
    isPercentCompleteDisabled: false,
    projectId: null,
  }
  @observable public projectColoringOptions: IProjectColoringOptions = {
    isWWPColoringDisabled: false,
    projectId: null,
    colors: [],
  }

  @observable
  public zoneMapThresholds: IZoneMapThresholds = {
    deltaDaysValue: 0,
    deltaDaysBehindValue: 0,
    deltaDaysUnit: ThresholdUnit.Number,
    manpowerValue: 0,
    manpowerUnit: ThresholdUnit.Number,
    isManpowerDisabled: false,
    projectId: null,
  }

  @observable
  public projectHistory: IProjectHistory = {
    lastUpdated: [],
    projectId: null,
  }

  @observable private _recipientIdForChatInit: string = ''
  @action.bound
  public setRecipientIdForChatInit(userId: string) {
    this._recipientIdForChatInit = userId
  }
  @action.bound
  public resetRecipientIdForChatInit() {
    this._recipientIdForChatInit = null
  }
  public get recipientIdForChatInit(): string {
    return this._recipientIdForChatInit
  }

  @observable private _idOfUserDisplayedOnGlobalCard: string = ''
  @action.bound
  public setIdOfUserDisplayedOnGlobalCard(userId: string) {
    this._idOfUserDisplayedOnGlobalCard = userId
  }
  @action.bound
  public resetIdOfUserDisplayedOnGlobalCard() {
    this._idOfUserDisplayedOnGlobalCard = null
  }
  public get idOfUserDisplayedOnGlobalCard(): string {
    return this._idOfUserDisplayedOnGlobalCard
  }

  @observable private _selectedChatChannelId: string = ''
  @action.bound
  public setSelectedChatChannelId(channelId: string) {
    this._selectedChatChannelId = channelId
  }
  @action.bound
  public resetSelectedChatChannelId() {
    this._selectedChatChannelId = null
  }
  public get selectedChatChannelId(): string {
    return this._selectedChatChannelId
  }

  @observable private _workflowIdDisplayedOnGlobalCard: string = ''
  @action.bound
  public setWorkflowIdDisplayedOnGlobalCard(workflowId: string) {
    this._workflowIdDisplayedOnGlobalCard = workflowId
  }
  @action.bound
  public resetWorkflowIdDisplayedOnGlobalCard() {
    this._workflowIdDisplayedOnGlobalCard = null
  }
  public get workflowIdDisplayedOnGlobalCard(): string {
    return this._workflowIdDisplayedOnGlobalCard
  }

  @observable private _resourceDisplayedOnGlobalCard: {
    id: string
    resourceTypeId: string
  } | null = null
  @action.bound
  public setResourceDisplayedOnGlobalCard(id: string, resourceTypeId: string) {
    this._resourceDisplayedOnGlobalCard = { id, resourceTypeId }
  }
  @action.bound
  public resetResourceDisplayedOnGlobalCard() {
    this._resourceDisplayedOnGlobalCard = null
  }
  public get resourceDisplayedOnGlobalCard() {
    return this._resourceDisplayedOnGlobalCard
  }

  @observable private _userActiveProjectSettings: UserProject = null
  @observable private _latitude: number = 0
  @observable private _longitude: number = 0
  private readonly integrationsConfigMap = observable(
    new Map<IntegrationAppName, IIntegrationConfig>(),
  )

  public constructor() {
    this.resetToDefaultDeliveryConfigurations()
    this.resetToDefaultLogisticsConfigurations()
    this.resetToDefaultWorkflowsConfigurations()
    makeObservable(this)
  }

  public set longitude(longitude: number) {
    this._longitude = longitude || 0
  }

  public get longitude(): number {
    return this._longitude
  }

  public set latitude(latitude: number) {
    this._latitude = latitude || 0
  }

  public get latitude(): number {
    return this._latitude
  }

  @computed
  public get activeTilesetsById(): Map<string, ITileset> {
    return new Map(this.activeTilesetsList.map(i => [i?.id, i]))
  }

  public get activeTilesetsList(): ITileset[] {
    return Array.from(this.tilesets.values()).filter(i => !!i?.mapboxData)
  }

  public get maxTilesetSizeInBytes() {
    return convertToBytes(this.tilesetsConfiguration?.maxSize)
  }

  public set userActiveProjectSettings(up: UserProject) {
    this._userActiveProjectSettings = up
  }

  public get userActiveProjectSettings(): UserProject {
    return this._userActiveProjectSettings
  }

  public get isPresentationMode(): boolean {
    return this.userActiveProjectSettings?.isPresentationUser
  }

  public getDefaultProjectHistory(): IProjectHistory {
    return {
      lastUpdated: [],
      projectId: this.activeProject.id,
    }
  }

  public getDefaultProjectMaterialOptions(): IProjectMaterialOptions {
    return {
      isCSIDisabled: false,
      isCSISubCategoriesDisabled: false,
      hiddenCategoryIds: [],
      hiddenSubCategoryIds: [],
      projectId: this.activeProject.id,
    }
  }

  public getDefaultProjectAddress(): IProjectAddress {
    return Object.assign(createEmptyAddress(), {
      projectId: this.activeProject.id,
    })
  }

  public getDefaultProjectTypeOptions(): IProjectTypeOptions {
    return {
      isDeliveriesDisabled: false,
      isTrackerDisabled: true,
      isLogisticsDisabled: false,
      isMaterialsDisabled: false,
      isMaterialResourcesDisabled: false,
      isEquipmentDisabled: false,
      isPhotosDisabled: false,
      isFormsDisabled: false,
      isAnalyticsDisabled: false,
      isStruxTagDisabled: false,
      isLocationsDisabled: false,
      projectId: this.activeProject.id,
    }
  }

  public getDefaultProjectStatusUpdateOptions(): IProjectStatusUpdateOptions {
    return {
      isAutoGenerated: true,
      isManpowerDisabled: false,
      isPercentCompleteDisabled: false,
      projectId: this.activeProject.id,
    }
  }

  public getDefaultProjectColoringOptions(): IProjectColoringOptions {
    return {
      isWWPColoringDisabled: false,
      projectId: this.activeProject.id,
      colors: [],
    }
  }

  public getDefaultZoneMapThresholds(): IZoneMapThresholds {
    return {
      deltaDaysValue: 0,
      deltaDaysBehindValue: 0,
      deltaDaysUnit: ThresholdUnit.Number,
      manpowerValue: 0,
      manpowerUnit: ThresholdUnit.Number,
      isManpowerDisabled: false,
      projectId: this.activeProject.id,
    }
  }

  public getFormattedFieldName = (field: string) => {
    const fromFieldIdToRegularMap = {
      [FieldIds.FROM_BUILDING]: FieldIds.BUILDING,
      [FieldIds.FROM_LEVEL]: FieldIds.LEVEL,
      [FieldIds.FROM_AREA]: FieldIds.AREA,
      [FieldIds.FROM_ZONE]: FieldIds.ZONE,
      [FieldIds.FROM_INSTALLATION_ZONE]: FieldIds.INSTALLATION_ZONE,
      [FieldIds.FROM_STAGING]: FieldIds.STAGING,
    }

    return fromFieldIdToRegularMap[field] || field
  }

  public getDeliveryFieldName = (field: string) => {
    const customName =
      this.delivery.customFieldNames[this.getFormattedFieldName(field)]

    if (customName) {
      return customName
    }

    switch (field) {
      case FieldIds.PROJECT_LOCATION:
        return Localization.translator.projectLocation
      case FieldIds.SITE_MAPS:
        return Localization.translator.deliverySitemap
      case FieldIds.GLOBES:
        return Localization.translator.globeViews
      case FieldIds.GATE:
        return Localization.translator.gate
      case FieldIds.ZONE:
      case FieldIds.FROM_ZONE:
        return Localization.translator.zone
      case FieldIds.INSTALLATION_ZONE:
      case FieldIds.FROM_INSTALLATION_ZONE:
        return Localization.translator.installationZone
      case FieldIds.TRUCK_SIZE:
        return Localization.translator.deliveryVehicleType
      case FieldIds.TRUCK_NUMBER:
        return Localization.translator.numberOfVehicles
      case FieldIds.TRUCK_LICENSE_PLATE:
        return Localization.translator.numberOfVehicleLicensePlate
      case FieldIds.OFFLOADING_EQUIPMENT:
        return Localization.translator.equipment
      case FieldIds.MATERIAL:
        return Localization.translator.material
      case FieldIds.MATERIAL_UNITS:
        return Localization.translator.units
      case FieldIds.MATERIAL_LOCATION:
        return Localization.translator.materialDeliveryLocation
      case FieldIds.IS_INSPECTION_REQUIRED:
        return Localization.translator.inspectionRequired
      case FieldIds.INSPECTOR:
        return Localization.translator.inspector
      case FieldIds.VENDOR:
        return Localization.translator.vendor
      case FieldIds.VENDOR_EMAILS:
        return Localization.translator.vendorEmails
      case FieldIds.DRIVER_PHONE_NUMBERS:
        return Localization.translator.driverMobilePhoneNumbers
      case FieldIds.NOTE:
        return Localization.translator.specialInstructions
      case FieldIds.CUSTOM_ACTION_BUTTON:
        return Localization.translator.customActionButton
      case FieldIds.BUILDING:
      case FieldIds.FROM_BUILDING:
        return Localization.translator.building
      case FieldIds.ROUTE:
        return Localization.translator.route
      case FieldIds.SITE:
        return Localization.translator.site
      case FieldIds.LOGISTICS_OBJECT:
        return Localization.translator.siteLocation
      case FieldIds.VERTICAL_OBJECT:
        return Localization.translator.verticalObject
      case FieldIds.LEVEL:
      case FieldIds.FROM_LEVEL:
        return Localization.translator.level
      case FieldIds.AREA:
      case FieldIds.FROM_AREA:
        return Localization.translator.area
      case FieldIds.FROM_TO_SWITCH:
        return Localization.translator.fromToDelivery
      case FieldIds.MATERIAL_NOTE:
        return Localization.translator.materialNote
      case FieldIds.NUMBER_OF_EQUIPMENT_PICKS:
        return Localization.translator.numberOfPicks
      case FieldIds.STAGING:
      case FieldIds.FROM_STAGING:
        return Localization.translator.staging
      case FieldIds.INTERIOR_DOOR:
        return Localization.translator.interiorDoor
      case FieldIds.INTERIOR_PATH:
        return Localization.translator.interiorPath

      // cannot be renamed in settings
      case FieldIds.DELIVERY_PROJECT_FILTER_OPTION:
        return Localization.translator.project
      case FieldIds.DATE:
        return Localization.translator.bookingDate
      case FieldIds.END_DATE:
        return Localization.translator.finishDate
      case FieldIds.START_TIME:
        return Localization.translator.startTime
      case FieldIds.END_TIME:
        return Localization.translator.endTime
      case FieldIds.COMPANY:
        return Localization.translator.requesterCompany
      case FieldIds.DURATION:
        return Localization.translator.duration
      case FieldIds.ON_SITE_CONTACTS:
        return Localization.translator.onsiteContacts
      case FieldIds.STATUS:
        return Localization.translator.status
      case FieldIds.MATERIAL_CATEGORY:
        return Localization.translator.materialsCategory
      case FieldIds.ID:
        return Localization.translator.deliveryId
      case FieldIds.BOOKING_TIME:
        return Localization.translator.bookingTime
      case FieldIds.MATERIAL_QUANTITY:
        return Localization.translator.quantity
      case FieldIds.CANCELATION_REASON:
        return Localization.translator.reason
      case FieldIds.INSPECTION_SECTION:
        return Localization.translator.inspection
      case FieldIds.LOCATION:
        return Localization.translator.location
      case FieldIds.MATERIAL_PROCUREMENT_ID:
        return Localization.translator.procurementID
      case FieldIds.PROJECT:
        return Localization.translator.project
      default:
        throw new Error(field + ' is not added to translator')
    }
  }

  @action
  public resetToDefaultCompactDeliveryView() {
    this.delivery.compactView.mode = DeliveryPageMode.VIEW
    this.delivery.compactView.highlightedDelivery = null
  }

  public resetToDefaultDeliveryConfigurations() {
    this.delivery.configurations = {
      deliveryDuration: 60,
      deadlineInterval: 1,
      maps: [],
      shouldBlockLateRequesting: false,
      onTimeWindow: 0,
      allColors: PRE_POPULATED_COLORS,
      projectId: this.activeProject.id,
      cardFieldsConfig: DEFAULT_DELIVERY_CARD_CONFIG,
      hiddenCancelationReasons: [],
      unextendableFields: [],
      isMultipleEquipmentAllowed: false,
    }
    this.delivery.isDataReceived = false
  }

  public updateDeliveryConfigurations(
    deliveryConfigurations: IDeliveryConfigurations,
  ) {
    this.resetToDefaultDeliveryConfigurations()

    const { configurations } = this.delivery

    if (deliveryConfigurations) {
      Object.assign(configurations, deliveryConfigurations)

      // TODO: Check the response from back
      // It seems like there is no case when key is exist but value is null
      if (!configurations.hiddenCancelationReasons) {
        configurations.hiddenCancelationReasons = []
      }

      this.updateCardConfig()
    }

    this.delivery.isDataReceived = true
  }

  public resetToDefaultLogisticsConfigurations() {
    this.logistics.configurations = {
      maps: [],
      projectId: this.activeProject.id,
    }
  }

  public resetToDefaultWorkflowsConfigurations() {
    this.workflows.configurations = {
      maps: [],
      projectId: this.activeProject.id,
    }
    this.workflows.isDataReceived = false
  }

  public updateWorkflowsConfigurations(
    workflowsConfigurations: IWorkflowConfigurations,
  ) {
    this.resetToDefaultWorkflowsConfigurations()

    const { configurations } = this.workflows

    if (workflowsConfigurations) {
      Object.assign(configurations, workflowsConfigurations)
    }

    this.workflows.isDataReceived = true
  }

  public updateLogisticsConfigurations(
    logisticsConfigurations: ILogisticsConfigurations,
  ) {
    this.resetToDefaultLogisticsConfigurations()

    const { configurations } = this.logistics

    if (logisticsConfigurations) {
      Object.assign(configurations, logisticsConfigurations)
    }
  }

  @action.bound
  public clearIntegrations() {
    this.integrationsConfigMap.clear()
  }

  @action.bound
  public receiveIntegrationConfig(integrationConfig: IIntegrationConfig) {
    try {
      if (!isIntegrationConfigValid(integrationConfig)) {
        throw Error(configErrorMessage)
      }

      this.integrationsConfigMap.set(
        integrationConfig.AppName,
        integrationConfig,
      )
    } catch (error) {
      console.warn(error.message)
    }
  }

  public isIntegrationEnabled = (appName: IntegrationAppName): boolean => {
    return (
      this.integrationsConfigMap.has(appName) &&
      this.integrationsConfigMap.get(appName).IsEnabled
    )
  }

  public get isWWPColoringDisabled(): boolean {
    return this.projectColoringOptions.isWWPColoringDisabled
  }

  public get isTrackerDisabled(): boolean {
    return true
  }

  public get isTilesetsDisabled(): boolean {
    const { allowTilesets } = this.featureFlags
    const isTilesetsAllowed =
      typeof allowTilesets === 'undefined' ? true : allowTilesets
    return !isTilesetsAllowed
  }

  public get isPhotosDisabled(): boolean {
    const { allowPhotos } = this.featureFlags
    const isPhotosAllowed =
      typeof allowPhotos === 'undefined' ? true : allowPhotos
    return this.projectTypeOptions.isPhotosDisabled || !isPhotosAllowed
  }

  public get isAnalyticsDisabled(): boolean {
    const { allowAnalytics } = this.featureFlags
    const isAnalyticsAllowed =
      typeof allowAnalytics === 'undefined' ? true : allowAnalytics
    return this.projectTypeOptions.isAnalyticsDisabled || !isAnalyticsAllowed
  }

  public get isDeliveriesDisabled(): boolean {
    const { allowDeliveriesToggling } = this.featureFlags
    const isDeliveriesAllowed =
      typeof allowDeliveriesToggling === 'undefined'
        ? true
        : allowDeliveriesToggling
    return this.projectTypeOptions.isDeliveriesDisabled || !isDeliveriesAllowed
  }

  public get isWorkflowsDisabled(): boolean {
    const { allowFormsToggling } = this.featureFlags
    const isWorkflowsAllowed =
      typeof allowFormsToggling === 'undefined' ? true : allowFormsToggling

    return this.projectTypeOptions.isFormsDisabled || !isWorkflowsAllowed
  }

  public get isThereFormsAssociatedMap(): boolean {
    return !!this.workflowsMapIdsList.length
  }

  private isMapConfigurationValid(m: IOrderedSitemap) {
    if (m.globeViewId) {
      return this.globeViews.has(m.globeViewId)
    }
    if (m.sitemapId) {
      return this.sitemaps.has(m.sitemapId)
    }
    return false
  }

  @computed
  public get logisticsMapIdsList(): IMapConfiguration[] {
    return (this.logistics.configurations.maps || [])
      .slice()
      .sort((a, b) => a.order - b.order)
      .filter(m => this.isMapConfigurationValid(m))
      .map(map => ({
        globeViewId: map.globeViewId,
        sitemapId: map.sitemapId,
      }))
  }

  @computed
  public get workflowsMapIdsList(): IMapConfiguration[] {
    return (this.workflows.configurations.maps || [])
      .slice()
      .sort((a, b) => a.order - b.order)
      .filter(m => this.isMapConfigurationValid(m))
      .map(map => ({
        globeViewId: map.globeViewId,
        sitemapId: map.sitemapId,
      }))
  }

  @computed
  public get workflowsMapGlobeViewIdsList(): string[] {
    return this.workflowsMapIdsList
      .filter(map => map.globeViewId)
      .map(map => map.globeViewId)
  }

  @computed
  public get deliveriesMapIdsList(): IMapConfiguration[] {
    return (this.delivery.configurations.maps || [])
      .slice()
      .sort((a, b) => a.order - b.order)
      .filter(m => this.isMapConfigurationValid(m))
      .map(map => ({
        globeViewId: map.globeViewId,
        sitemapId: map.sitemapId,
      }))
  }

  public get isStatusUpdateManpowerDisabled(): boolean {
    return this.projectStatusUpdateOptions.isManpowerDisabled
  }

  public get isLogisticsDisabled(): boolean {
    const { allowLogisticsToggling } = this.featureFlags
    const isLogisticsAllowed =
      typeof allowLogisticsToggling === 'undefined'
        ? true
        : allowLogisticsToggling
    return this.projectTypeOptions.isLogisticsDisabled || !isLogisticsAllowed
  }

  public get isMaterialsDisabled(): boolean {
    const { allowMaterialsToggling } = this.featureFlags
    const isMaterialsAllowed =
      typeof allowMaterialsToggling === 'undefined'
        ? isTestEnvironment()
        : allowMaterialsToggling
    return this.projectTypeOptions.isMaterialsDisabled || !isMaterialsAllowed
  }

  public get isMaterialResourcesDisabled(): boolean {
    const { allowMaterialResourcesToggling } = this.featureFlags
    const isMaterialResourcesAllowed =
      typeof allowMaterialResourcesToggling === 'undefined'
        ? isTestEnvironment()
        : allowMaterialResourcesToggling
    return (
      !!this.projectTypeOptions.isMaterialResourcesDisabled ||
      !isMaterialResourcesAllowed
    )
  }

  public get isEquipmentDisabled(): boolean {
    const { allowEquipment } = this.featureFlags
    const isEquipmentAllowed =
      typeof allowEquipment === 'undefined' ? true : allowEquipment
    return this.projectTypeOptions.isEquipmentDisabled || !isEquipmentAllowed
  }

  public get isStruxTagsDisabled(): boolean {
    const { allowStruxTags } = this.featureFlags
    const isStruxTagsAllowed =
      typeof allowStruxTags === 'undefined' ? true : allowStruxTags
    return this.projectTypeOptions.isStruxTagDisabled || !isStruxTagsAllowed
  }

  public get isLocationsDisabled(): boolean {
    const { allowLocations } = this.featureFlags
    const isLocationsAllowed =
      typeof allowLocations === 'undefined' ? true : allowLocations
    return this.projectTypeOptions.isLocationsDisabled || !isLocationsAllowed
  }

  public get isWorkflowSummaryEnabled(): boolean {
    const { allowWorkflowSummary } = this.featureFlags
    return typeof allowWorkflowSummary === 'undefined'
      ? false
      : allowWorkflowSummary
  }

  public get activeOrInitProjectCode(): string {
    return this.activeProject.code || this.initProjectCode
  }

  @computed
  public get isLoading(): boolean {
    return Array.from(this.loading.values()).some(value => value)
  }

  public get isStruxhubUser(): boolean {
    return !!this.user?.email?.endsWith('@struxhub.com')
  }

  public get isSTXAdmin(): boolean {
    return this.userActiveProjectSettings.isAdmin && this.isStruxhubUser
  }

  private updateCardConfig() {
    const { configurations } = this.delivery

    if (!configurations.cardFieldsConfig?.length) {
      configurations.cardFieldsConfig = DEFAULT_DELIVERY_CARD_CONFIG
    } else {
      const missedFields = DEFAULT_DELIVERY_CARD_CONFIG.filter(
        cardField =>
          !configurations.cardFieldsConfig.some(
            ({ fieldId }) => fieldId === cardField.fieldId,
          ),
      )
      configurations.cardFieldsConfig.push(...missedFields)
    }

    configurations.cardFieldsConfig.every(dc => delete dc['__typename'])
  }
}

class CompactDeliveryView {
  @observable public mode: DeliveryPageMode = DeliveryPageMode.VIEW
  @observable public viewMode: DeliveryPageViewMode =
    DeliveryPageViewMode.Calendar
  @observable public highlightedDelivery: Delivery = null
  @observable public activeDate: Date = new Date()
  @observable public activeEndDate: Date = new Date()

  public constructor() {
    makeObservable(this)
  }

  public get isViewWithHighlightedDeliveryMode() {
    return (
      this.mode === DeliveryPageMode.VIEW_WITH_HIGHLIGHTED_DELIVERY &&
      this.highlightedDelivery
    )
  }

  public resetViewMode() {
    this.viewMode = DeliveryPageViewMode.Calendar
  }
}

export interface IFilters {
  searchKey: string
  sortingKey?: string
  timeKey?: IDateTimeFilter
  dateKey?: IDateTimeFilter
  iconKey?: string
  groupingKey?: string
  fieldsMap?: {
    [filterType: string]: UIFilterInfo
  }
}

export interface ISharedFilters {
  selectedRecentlyUpdatedMode: RecentlyUpdatedMode
  recentlyUpdatedStartDate: Date
  recentlyUpdatedEndDate: Date
}
